import { useState, useEffect } from 'react';
import { getPublicAvatarUrl } from '../utils/publicAvatarUtils';
import avatarCacheSystem, { getCachedAvatarUrl } from '../utils/avatarCacheSystem';

/**
 * 통합 아바타 캐시를 활용하는 최적화된 useCachedAvatar 훅
 * - 원본 해상도 우선 캐싱
 * - 점진적 로딩 (작은 이미지 → 원본)
 * - 통합 캐시 시스템 활용
 */
export const useCachedAvatar = (userID, fallbackUrl = null, size = 160) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadAvatar = async () => {
      if (!userID) {
        setImageSrc(fallbackUrl);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        // 🎯 1단계: 통합 캐시에서 확인 (원본 우선)
        let cachedUrl = getCachedAvatarUrl(userID, size);

        if (cachedUrl) {
          // 캐시된 이미지가 있으면 즉시 사용
          setImageSrc(cachedUrl);
          setLoading(false);
          setError(false);
          return;
        }

        // 🚀 2단계: 점진적 로딩 전략
        // WriterInfo는 상대적으로 작은 사이즈이므로, 빠른 로딩 우선
        const quickLoadSize = Math.min(size, 160); // 최대 160px로 제한
        const quickUrl = getPublicAvatarUrl(userID, quickLoadSize);
        
        // 작은 이미지 로드
        const quickImg = new Image();
        quickImg.crossOrigin = 'anonymous';
        
        quickImg.onload = () => {
          setImageSrc(quickUrl);
          setLoading(false);
          
          // 🎯 3단계: 백그라운드에서 원본 해상도 이미지 캐싱 (선택적)
          if (size > quickLoadSize) {
            loadHighResolution(userID, size);
          } else {
            // 작은 사이즈도 백그라운드에서 원본 캐싱
            avatarCacheSystem.batchPreload([userID]);
          }
        };
        
        quickImg.onerror = () => {
          // 작은 이미지도 실패하면 원본 캐시에서 시도
          tryOriginalFromCache(userID, size);
        };
        
        quickImg.src = quickUrl;

      } catch (error) {
        console.error('Avatar loading failed:', error);
        setError(true);
        setImageSrc(fallbackUrl);
        setLoading(false);
      }
    };

    // 고해상도 이미지 백그라운드 로드 (비동기)
    const loadHighResolution = async (userID, targetSize) => {
      try {
        // 통합 캐시 시스템을 통해 원본 이미지 preload
        await avatarCacheSystem.preloadAvatar(userID);
        
        // 원본이 캐시되면 고해상도 URL 가져오기
        const highResUrl = getCachedAvatarUrl(userID, targetSize);
        
        if (highResUrl && highResUrl !== imageSrc) {
          // 부드러운 전환을 위해 이미지 검증 후 교체
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          img.onload = () => {
            setImageSrc(highResUrl);
            console.log(`✨ WriterInfo upgraded to high-res avatar: ${userID} (${targetSize}px)`);
          };
          
          img.onerror = () => {
            console.warn(`Failed to load high-res WriterInfo avatar: ${userID}`);
          };
          
          img.src = highResUrl;
        }
      } catch (error) {
        // 고해상도 로딩 실패는 치명적이지 않음 (이미 작은 이미지로 표시 중)
        console.warn('High-resolution WriterInfo avatar loading failed:', error);
      }
    };

    // 원본 캐시에서 직접 시도
    const tryOriginalFromCache = async (userID, targetSize) => {
      try {
        // 원본 이미지 preload 시도
        const originalUrl = await avatarCacheSystem.preloadAvatar(userID);
        
        if (originalUrl) {
          const finalUrl = targetSize === 512 ? originalUrl : getPublicAvatarUrl(userID, targetSize);
          setImageSrc(finalUrl);
          setLoading(false);
          setError(false);
        } else {
          throw new Error('Failed to load from cache');
        }
      } catch (error) {
        console.error('Original cache loading failed:', error);
        setError(true);
        setImageSrc(fallbackUrl);
        setLoading(false);
      }
    };

    loadAvatar();
  }, [userID, fallbackUrl, size]);

  return { 
    imageSrc, 
    loading, 
    error,
    // 추가 정보 제공
    isCached: userID ? avatarCacheSystem.isCached(userID) : false
  };
};